import { createTheme } from '@mui/material';
import {
  ColorBeige,
  ColorBlue,
  ColorBluViu,
  ColorDarkBlue,
  ColorError,
  ColorGray,
  ColorGreen,
  ColorInfo,
  ColorLightBlue,
  ColorLightGray,
  ColorLightGreen,
  ColorOrange,
  ColorPrimary,
  ColorPurple,
  ColorRed,
  ColorSecondary,
  ColorSuccess,
  ColorWarning,
} from './theme-colors';

import '@mui/x-date-pickers/themeAugmentation';

// ---------------------------------------------------
// Base Theme Configuration
// This theme contains all the common properties shared between salmonTheme and shrimpTheme.
// ---------------------------------------------------
export const baseTheme = createTheme({
  zIndex: {
    snackbar: 1900,
    modal: 1500,
  },
  typography: {
    fontFamily: 'DM Sans, Arial',
    htmlFontSize: 10,
    fontSize: 16,
    h1: {
      fontSize: 32,
      lineHeight: 1,
      fontWeight: 500,
      letterSpacing: 0.9,
    },
    h2: {
      fontSize: 24,
      lineHeight: 1,
      fontWeight: 500,
      letterSpacing: -0.59,
    },
    h3: {
      fontSize: 20,
      fontWeight: 500,
    },
    h4: {
      fontSize: 18,
      fontWeight: 500,
    },
    h5: {
      fontSize: 16,
      fontWeight: 500,
    },
    h6: {
      fontSize: 16,
      fontWeight: 500,
    },
    body1: {
      fontSize: 16,
    },
    body2: {
      fontSize: 16,
    },
    caption: {
      display: 'block',
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: 0.74,
      textTransform: 'none',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1400,
    },
  },
  spacing: 4,
  palette: {
    gradient:
      'linear-gradient(to bottom, #004a98, #004485, #003c71, #00355d, #002d4a, #002a42, #002639, #012231, #012231, #012231, #012231, #012231);',
    mode: 'light',
    common: {
      black: '#333333',
      white: '#ffffff',
    },
    primary: ColorPrimary,
    secondary: ColorSecondary,
    info: ColorInfo,
    success: ColorSuccess,
    error: ColorError,
    warning: ColorWarning,
    blue: ColorBlue,
    purple: ColorPurple,
    lightGreen: ColorLightGreen,
    lightGray: ColorLightGray,
    gray: ColorGray,
    bluViu: ColorBluViu,
    darkBlue: ColorDarkBlue,
    beige: ColorBeige,
    red: ColorRed,
    orange: ColorOrange,
    green: ColorGreen,
    lightBlue: ColorLightBlue,
    background: {
      // default: '#ffffff',
      // main: 'rgb(1,73,148)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: () => ({
        body: {},
        a: {
          textDecoration: 'none !important',
          '&:hover': {
            textDecoration: 'none',
          },
        },
      }),
    },
    MuiTextField: {
      defaultProps: {
        hiddenLabel: true,
        size: 'medium',
        margin: 'none',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        input: {
          flex: 1,
          '&[type=number]': {
            appearance: 'textfield',
          },
          '&::-webkit-outer-spin-button': {
            appearance: 'none',
          },
          '&::-webkit-inner-spin-button': {
            appearance: 'none',
          },
          '& small': {
            display: 'none',
          },
        },
        notchedOutline: {
          borderWidth: 1,
          borderStyle: 'solid',
        },
        multiline: {},
        inputMultiline: {},
        adornedStart: {
          paddingLeft: 10,
          flexWrap: 'nowrap',
        },
        adornedEnd: {
          flexWrap: 'nowrap',
        },
        inputAdornedStart: {
          paddingLeft: 5,
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slotProps: {
          desktopPaper: {
            sx: {
              padding: 2,
            },
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        slotProps: {
          toolbar: {
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: {
              debounceMs: 200,
            },
          },
        },
      },
      styleOverrides: {
        root: {
          fontSize: 12,
          borderRadius: 8,
          border: `1px solid ${ColorDarkBlue.main}`,
          '& MuiDataGrid-cellEmpty': {
            border: 'none',
          },
          '& svg': {
            color: ColorDarkBlue.main,
          },
        },
        toolbar: {
          padding: 16,
          borderBottom: `1px solid ${ColorDarkBlue.main}`,
        },
        toolbarQuickFilter: {
          '& .MuiFormControl-root': {
            minWidth: 0,
          },
        },
        columnSeparator: {
          display: 'none',
        },
        columnHeaders: {
          '& .MuiDataGrid-filler': {
            backgroundColor: `${ColorBlue.o50} !important`,
          },
        },
        columnHeaderTitle: {
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordBreak: 'keep-all',
        },
        columnHeader: {
          backgroundColor: ColorBlue.o50,
          borderRight: `1px solid ${ColorDarkBlue.main}`,
          height: 'auto',
          '&.MuiDataGrid-columnHeader--last': {
            borderRight: 'none',
          },
        },
        cell: {
          borderTop: `1px solid ${ColorDarkBlue.main}`,
          borderRight: `1px solid ${ColorDarkBlue.main}`,
          ':nth-last-of-type(2), :nth-last-of-type(1)': {
            // MUI appends a final cell with className 'MuiDataGrid-cellEmpty'. This causes the preceding cell (the last visible) to continue having borderRight, running against the border of the DataGrid. This overrides that so only one border is present
            borderRight: 'none',
          },
          ['&.cell--disabled']: {
            backgroundColor: `${ColorDarkBlue.light} !important`,
          },
          '& .Mui-error': {
            border: `1px solid ${ColorRed.main}`,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
          textDecoration: 'none',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 42,
          height: 26,
          padding: 0,
          borderRadius: '20px',
          border: `1px solid ${ColorDarkBlue.main}`,
          '& .MuiSwitch-thumb': {
            width: 15,
            height: 15,
            boxShadow: 'none',
          },
          '& .MuiSwitch-track': {
            borderRadius: 13,
            opacity: 1,
            backgroundColor: 'white',
            transition: theme.transitions.create(['background-color'], {
              duration: 500,
            }),
          },
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            border: `1px solid ${ColorDarkBlue.o25}`,
            '& .MuiSwitch-thumb': {
              backgroundColor: ColorDarkBlue.o25,
            },
            '.MuiSwitch-track': {
              backgroundColor: 'transparent !important',
            },
          },
          '&.Mui-error': {
            border: `1px solid ${ColorError.dark}`,
            '& .MuiSwitch-thumb': {
              backgroundColor: `${ColorError.dark} !important`,
            },
            '.MuiSwitch-track': {
              backgroundColor: 'transparent !important',
            },
          },
        }),
        switchBase: ({ theme }) => ({
          padding: 0,
          margin: '4px 5px',
          backgroundColor: 'transparent',
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            '& .MuiSwitch-thumb': {
              backgroundColor: ColorDarkBlue.main,
            },
            '& + .MuiSwitch-track': {
              backgroundColor: 'white',
              opacity: 1,
            },
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: ColorDarkBlue.o25,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          '&.Mui-backgroundLight': {
            color: 'white',
          },
          '&.Mui-backgroundDark': {
            color: ColorDarkBlue.main,
          },
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            color: `${ColorDarkBlue.o50} !important`,
            '&:hover': {
              color: `${ColorDarkBlue.o50} !important`,
            },
          },
          '&.Mui-error': {
            color: `${ColorError.dark} !important`,
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 4,
          padding: '20px 0',
          marginBottom: 0,
        },
        thumb: {
          height: 10,
          width: 10,
          marginTop: -4,
          marginLeft: -4,
          '&:focus, &:hover, &$active': {
            boxShadow: '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)',
          },
          transform: 'translate(-50%, -15%)',
        },
        marked: {
          marginBottom: 0,
        },
        mark: {
          height: 4,
        },
        track: {
          height: 4,
        },
        rail: {
          height: 4,
        },
        active: {},
        valueLabel: {
          top: 0,
          backgroundColor: 'transparent',
          '& *': {
            background: 'transparent',
            color: ColorPrimary.main,
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '1px',
          },
        },
        markLabel: {
          top: 28,
          fontSize: 10,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
        },
        list: {
          padding: '10px 5px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          height: 50,
          fontSize: 16,
          '&.Mui-selected': {
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: ColorLightBlue.o25,
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'white',
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'white',
          },
          '&:hover': {
            backgroundColor: ColorLightBlue.o25,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root th:first-of-type': {
            paddingLeft: 16,
          },
          '& .MuiTableRow-root th:last-of-type': {
            paddingRight: 16,
          },
          '&.Mui-backgroundLight': {
            '& .MuiTableCell-head': {
              color: 'white',
              '& svg': {
                color: 'white',
                '&:hover': {
                  color: 'white',
                },
              },
              '&:hover': {
                color: 'white',
              },
            },
          },
          '&.Mui-backgroundDark': {
            '& .MuiTableCell-head': {
              color: ColorDarkBlue.main,
              '& svg': {
                color: ColorDarkBlue.main,
                '&:hover': {
                  color: ColorDarkBlue.main,
                },
              },
              '&:hover': {
                color: ColorDarkBlue.main,
              },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        sizeSmall: {
          padding: '5px 10px',
        },
        root: {
          border: 'none',
        },
        head: {
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.7,
          lineHeight: 1.25,
          padding: 8,
          '&.HeadCell-autoWidth': {
            width: 0,
          },
          '&.HeadCell-fullWidth': {
            width: '100%',
          },
          '&.HeadCell-sticky': {
            position: 'sticky',
            left: 0,
            zIndex: 1, // Ensure sticky cells stay on top
            backgroundColor: 'inherit', // Ensure the background matches the table
          },
          '& div': {
            verticalAlign: 'bottom',
            '& span': {
              display: 'inline-flex',
            },
          },
          // Align checkboxes in table headers with row checkboxes by removing MUI's default spacing
          '& .MuiFormControlLabel-root': {
            margin: 0,
            marginLeft: -2,
            '& .MuiCheckbox-root': {
              padding: 0,
            },
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: 'inherit', // Inherit hover color from parent
          },
          '&.Mui-active': {
            color: 'inherit', // Keep active color consistent
            '& .MuiTableSortLabel-icon': {
              color: 'inherit', // Ensure the icon color matches the text color
            },
          },
        },
        icon: {
          opacity: 0.1,
          color: 'inherit', // Inherit color from parent
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          verticalAlign: 'bottom',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 16,
          textTransform: 'none',
          color: 'white',
          textDecoration: 'none',
          '&.Mui-selected': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: '#ffffff16',
            color: 'white',
            fontWeight: 700,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        placement: 'top',
        arrow: true,
        enterDelay: 250,
        enterNextDelay: 250,
      },
      styleOverrides: {
        arrow: {
          '&:before': {
            backgroundColor: '#ffffff',
            border: `1px solid ${ColorDarkBlue.main}`,
            borderRadius: 2,
          },
        },
        tooltip: {
          fontSize: 16,
          padding: '15px',
          fontWeight: '500',
          border: `1px solid ${ColorDarkBlue.main}`,
          backgroundColor: '#ffffff',
          color: ColorDarkBlue.main,
          borderRadius: 16,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: 15,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          position: 'relative',
          padding: theme.spacing(4),
          borderRadius: '1rem',
          backgroundColor: theme.palette.common.white,

          '&.transparent': {
            backgroundColor: '#ffffff16',
            color: theme.palette.common.white,
          },
        }),
        rounded: {
          borderRadius: 8,
        },
        outlined: {
          border: 0,
        },
        elevation1: {
          boxShadow: '0 1px 2px -1px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        grouped: {
          minWidth: 40,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible .MuiTouchRipple-root': {
            borderWidth: 2,
            borderStyle: 'solid',
            display: 'block',
          },
          '&.MuiPickersDay-root.Mui-selected': {
            backgroundColor: `#012231 !important`,
            '&:hover': {
              backgroundColor: '#012231',
            },
            '&.MuiPickersDay-today': {
              border: 'solid 2px #012231',
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
        disableElevation: true,
        variant: 'contained',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          color: ColorBluViu.main,
          fontSize: 16,
          minWidth: '100px',
          borderRadius: 40,
          padding: '8px 24px',
          lineHeight: '1.5',
          textTransform: 'none',
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          },
        },
        startIcon: {
          marginLeft: 0,
          '& svg': {
            color: 'currentColor',
          },
        },
        endIcon: {
          flexGrow: 1,
          justifyContent: 'flex-end',
          '& svg': {
            color: 'currentColor',
          },
        },
        sizeSmall: {
          minHeight: 32,
        },
        sizeMedium: {
          minHeight: 48,
        },
        sizeLarge: {
          minHeight: 64,
        },
        contained: {
          backgroundColor: ColorLightBlue.o75,
          '&:hover': {
            backgroundColor: ColorLightBlue.main,
          },
          '&:disabled': {
            backgroundColor: ColorDarkBlue.o25,
            color: ColorDarkBlue.o75,
          },
        },
        outlined: {
          borderColor: ColorBluViu.main,
          color: ColorBluViu.main,
          '&:hover': {
            borderColor: ColorBluViu.main,
            color: ColorBluViu.main,
          },
          '&:disabled': {
            borderColor: ColorBluViu.main,
            backgroundColor: ColorDarkBlue.o25,
            color: ColorDarkBlue.o75,
          },
        },
        text: {
          lineHeight: 1.2,
          color: ColorBluViu.main,
          border: 'none',
          '&:hover': {
            background: 'transparent',
            color: ColorBluViu.main,
          },
          '&:disabled': {
            color: ColorDarkBlue.o75,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          color: ColorBluViu.main,
          background: 'none',
          '&:hover': {
            backgroundColor: 'none',
          },
          '@media (hover: none)': {
            '&:hover': {
              backgroundColor: `none !important`,
            },
          },
        },
        colorSecondary: {
          '&:hover': {
            backgroundColor: ColorOrange.main,
          },
          '@media (hover: none)': {
            '&:hover': {
              backgroundColor: `${ColorOrange.main} !important`,
            },
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          '& svg': {
            color: ColorDarkBlue.main,
          },
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          paddingBottom: 16,
        },
        button: {
          '&:hover': {
            backgroundColor: ColorDarkBlue.o25,
          },
          '&.Mui-selected': {
            '&:focus, &:hover': {
              backgroundColor: ColorDarkBlue.main,
            },
          },
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        button: {
          '&:hover': {
            backgroundColor: ColorDarkBlue.o25,
          },
          '&.Mui-selected': {
            '&:focus, &:hover': {
              backgroundColor: ColorDarkBlue.main,
            },
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: ColorError.dark,
          },
        },
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: ({}) => ({
          '&.Mui-backgroundLight': {
            '& *': {
              color: 'white',
            },
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '& svg': {
              color: 'white',
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `white !important`,
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `white !important`,
            },
            '&:hover svg': {
              color: `white !important`,
            },
            '&.Mui-focused svg': {
              color: `white !important`,
            },
            '&:hover input': {
              color: `white !important`,
            },
            '&.Mui-focused input': {
              color: `white !important`,
            },
          },
          '&.Mui-backgroundDark': {
            backgroundColor: 'white',
            '& *': {
              color: ColorDarkBlue.main,
            },
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: ColorDarkBlue.main,
            },
            '& svg': {
              color: ColorDarkBlue.main,
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `${ColorDarkBlue.main} !important`,
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `${ColorDarkBlue.main} !important`,
            },
            '&:hover svg': {
              color: `${ColorDarkBlue.main} !important`,
            },
            '&.Mui-focused svg': {
              color: `${ColorDarkBlue.main} !important`,
            },
            '&:hover input': {
              color: `${ColorDarkBlue.main} !important`,
            },
            '&.Mui-focused input': {
              color: `${ColorDarkBlue.main} !important`,
            },
          },
          '&.Mui-disabled': {
            '& *': {
              color: `${ColorDarkBlue.o50} !important`,
            },
            cursor: 'not-allowed',
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `${ColorDarkBlue.o50} !important`,
            },
            '& svg': {
              color: `${ColorDarkBlue.o50} !important`,
            },
            '& input': {
              '-webkit-text-fill-color': `unset !important`,
              color: `${ColorDarkBlue.o50} !important`,
            },
            // Ensure disabled state persists on hover
            '&:hover': {
              // The above rules for Mui-disabled combined with higher specificity
              // should already override general hover states. Explicitly restating
              // them here acts as a strong assurance for disabled-on-hover.
              color: `${ColorDarkBlue.o50} !important`,
              '& .MuiPickersOutlinedInput-notchedOutline': {
                borderColor: `${ColorDarkBlue.o50} !important`,
              },
              '& svg': {
                color: `${ColorDarkBlue.o50} !important`,
              },
              '& input': {
                '-webkit-text-fill-color': `unset !important`,
                color: `${ColorDarkBlue.o50} !important`,
              },
            },
          },
          '&.Mui-error': {
            '& *': {
              color: ColorError.dark,
            },
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: ColorError.dark,
            },
            '& svg': {
              color: ColorError.dark,
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `${ColorError.dark} !important`,
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: `${ColorError.dark} !important`,
            },
            '&:hover svg': {
              color: `${ColorError.dark} !important`,
            },
            '&.Mui-focused svg': {
              color: `${ColorError.dark} !important`,
            },
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&[data-shrink=true]': {
            width: 'auto',
          },
          '&[data-shrink=false]': {
            maxWidth: 'calc(100% - 40px);',
          },
          '&.Mui-backgroundLight': {
            color: 'white',
            '&.Mui-focused': {
              color: `white !important`,
            },
          },
          '&.Mui-backgroundDark': {
            color: ColorDarkBlue.main,
            '&.Mui-focused': {
              color: `${ColorDarkBlue.dark} !important`,
            },
          },
          // CONSOLIDATED DISABLED STYLES FOR MuiFormLabel
          '&.Mui-disabled': {
            color: `${ColorDarkBlue.o50} !important`,
            '&:hover': {
              color: `${ColorDarkBlue.o50} !important`, // Ensure disabled color on hover
            },
          },
          '&.Mui-error': {
            color: `${ColorError.dark} !important`,
            '&.Mui-focused': {
              color: `${ColorError.dark} !important`,
            },
          },
        },
      },
    },
    MuiDivider: {
      defaultProps: {
        'aria-hidden': true,
      },
      styleOverrides: {
        light: {
          backgroundColor: '#eeeeee',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
        margin: 'none',
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root, & .MuiCircularProgress-root': {
            margin: '0 10px',
            right: 0,
            position: 'absolute',
            pointerEvents: 'none',
          },
        },
        select: {
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
      },
    },
    // CONSOLIDATED MuiInputBase
    MuiInputBase: {
      styleOverrides: {
        input: {
          // Applies only to non-multiline inputs
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          position: 'relative',
        },
        root: ({ ownerState }) => ({
          '& textarea': {
            // Applies only to multiline inputs
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflow: 'auto',
          },
          '&.Mui-backgroundLight': {
            color: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '& .MuiInputAdornment-root': {
              color: 'white',
              '& .MuiTypography-root': {
                color: 'white',
              },
            },
            '& svg': {
              color: 'white',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `white !important`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `white !important`,
            },
            '&:hover svg': {
              color: `white !important`,
            },
            '&.Mui-focused svg': {
              color: `white !important`,
            },
            '&:hover input': {
              color: `white !important`,
            },
            '&.Mui-focused input': {
              color: `white !important`,
            },
          },
          '&.Mui-backgroundDark': {
            backgroundColor: 'white',
            color: ColorDarkBlue.main,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: ColorDarkBlue.main,
            },
            '& .MuiInputAdornment-root': {
              color: ColorDarkBlue.o75,
              '& .MuiTypography-root': {
                color: ColorDarkBlue.o75,
              },
            },
            '& svg': {
              color: ColorDarkBlue.main,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${ColorDarkBlue.main} !important`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${ColorDarkBlue.main} !important`,
            },
            '&:hover svg': {
              color: `${ColorDarkBlue.main} !important`,
            },
            '&.Mui-focused svg': {
              color: `${ColorDarkBlue.main} !important`,
            },
            '&:hover input': {
              color: `${ColorDarkBlue.main} !important`,
            },
            '&.Mui-focused input': {
              color: `${ColorDarkBlue.main} !important`,
            },
          },
          // CONSOLIDATED DISABLED STYLES FOR MuiInputBase
          '&.Mui-disabled': {
            '& *': {
              color: `${ColorDarkBlue.o50} !important`,
              '-webkit-text-fill-color': 'unset !important',
            },
            cursor: 'not-allowed',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${ColorDarkBlue.o50} !important`,
            },
            '& svg': {
              color: `${ColorDarkBlue.o50} !important`,
            },
            '& input': {
              cursor: 'not-allowed',
              // Specific for the input element itself
              '-webkit-text-fill-color': `${ColorDarkBlue.o50} !important`,
              color: `${ColorDarkBlue.o50} !important`,
            },
            // Ensure disabled state persists on hover for the entire base
            '&:hover': {
              // The above rules for Mui-disabled combined with higher specificity
              // should already override general hover states. Explicitly restating
              // them here acts as a strong assurance for disabled-on-hover.
              color: `${ColorDarkBlue.o50} !important`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${ColorDarkBlue.o50} !important`,
              },
              '& svg': {
                color: `${ColorDarkBlue.o50} !important`,
              },
              '& input': {
                '-webkit-text-fill-color': `${ColorDarkBlue.o50} !important`,
                color: `${ColorDarkBlue.o50} !important`,
              },
            },
          },
          // Universal Error State (applied regardless of backgroundStyle)
          '&.Mui-error': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${ColorError.dark} !important`,
            },
            '& *': {
              // Applies to all children, including input and adornments
              color: `${ColorError.dark} !important`,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${ColorError.dark} !important`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${ColorError.dark} !important`,
            },
            '&:hover svg': {
              color: `${ColorError.dark} !important`,
            },
            '&.Mui-focused svg': {
              color: `${ColorError.dark} !important`,
            },
            '&:hover input': {
              color: `${ColorError.dark} !important`,
            },
            '&.Mui-focused input': {
              color: `${ColorError.dark} !important`,
            },
          },
        }),
      },
    },
    MuiSvgIcon: {},
    // MuiInputLabel removed as it's a duplicate of MuiFormLabel
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          '&.Mui-backgroundLight': {
            color: 'white', // Set color for light background
          },
          '&.Mui-backgroundDark': {
            color: ColorDarkBlue.main, // Set color for dark background
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          marginTop: '4px',

          '&.Mui-backgroundLight': {
            color: 'white',
          },
          '&.Mui-backgroundDark': {
            color: ColorDarkBlue.main,
          },
          '&.Mui-disabled': {
            color: `${ColorDarkBlue.o50} !important`,
            '&:hover': {
              color: `${ColorDarkBlue.o50} !important`,
            },
          },
          '&.Mui-error': {
            color: `${ColorError.dark} !important`,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        marginNormal: {
          marginTop: 16,
          marginBottom: 16,
        },
        marginDense: {
          marginTop: 8,
          marginBottom: 8,
        },
      },
      variants: [
        {
          props: { variant: 'outlined', fullWidth: false },
          style: {
            minWidth: 200,
            maxWidth: 320,
          },
        },
        {
          props: { variant: 'outlined', fullWidth: true },
          style: {
            minWidth: 200,
            maxWidth: 'none',
          },
        },
      ],
    },
    MuiInputAdornment: {}, // No specific changes needed here as the MuiInputBase rules should cover it.
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: 40,
        },
        paperScrollPaper: {
          maxHeight: `calc(100% - 215px)`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 18,
          padding: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
          overflowY: 'visible',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {},
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          paper: {
            sx: {
              padding: 2,
            },
          },
        },
      },
      styleOverrides: {
        groupLabel: {
          color: ColorDarkBlue.o75,
        },
        option: {
          color: ColorDarkBlue.main,

          '&.Mui-disabled': {
            color: ColorDarkBlue.o50,
            opacity: 1,
          },

          '&[aria-selected="true"]': {
            color: ColorDarkBlue.main,
          },

          '&.Mui-focused': {
            color: ColorDarkBlue.main,
          },

          '& svg': {
            color: ColorDarkBlue.main,
            stroke: ColorDarkBlue.main,
          },
        },
        popupIndicator: {
          color: ColorDarkBlue.main,
        },
        clearIndicator: {
          color: ColorDarkBlue.main,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          width: '100%',
          marginBottom: 16,
        },
        standardInfo: {
          backgroundColor: ColorLightBlue.dark,
          '& .MuiAlert-icon': {
            color: ColorBlue.main,
          },
        },
        standardSuccess: {
          backgroundColor: ColorPrimary.light,
          '& .MuiAlert-icon': {
            color: ColorPrimary.main,
          },
        },
        standardWarning: {
          backgroundColor: ColorSecondary.light,
          '& .MuiAlert-icon': {
            color: ColorSecondary.main,
          },
        },
        standardError: {
          backgroundColor: ColorError.dark,
          '& .MuiAlert-icon': {
            color: ColorError.dark,
          },
        },
        icon: {
          alignItems: 'center',
        },
        message: {
          fontSize: 16,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          marginBottom: 0,
        },
      },
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          width: 'auto',
          height: 'auto',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { justifyContent: 'flex-start' },
      },
    },
    MuiTouchRipple: {
      styleOverrides: {
        root: {
          display: 'none',
          border: '6px solid #477eff',
        },
        ripple: {
          display: 'none',
        },
      },
    },
  },
});
